import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';

import { MedicalReviewModal, StatusCell, TagColor } from '@equip.health/ui';
import {
  DiagnosisOptions,
  MedicalReviewState,
} from '@equip.health/ui/lib/types/src/maud/modals/MedicalReview/MedicalReviewModal';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';
import { MedicalStatusOptions } from '~/lib/constants/inquiry.constants';
import { isEqualIgnoreNil, reducer, toDate } from '~/lib/util/inquiry.util';
import { useGetInquiryReviewsQuery } from '~/store/services/inquiry';

interface MedicalClearanceStatusCellProps {
  diagnosisOptions: DiagnosisOptions;
  id: string;
  inquiryId: string;
  medicalNotClearedReasonOptions: string[];
  medicalReview: {
    statusCode?: InquiryEnumV2;
    typeCode?: InquiryEnumV2;
    id?: string;
    description?: string;
  };
  medicalStatusOptions: CoverageStatusOptionsProps[];
  medicalTypeOptions: string[];
  updateMedicalReviewStatus?: (
    inquiryId: string,
    patientId: string,
    status: string,
    version: string,
  ) => void;
  updateMedicalReview?: (
    inquiryId: string,
    medicalEditState: MedicalReviewState,
    patientId: string,
    version: string,
  ) => Promise<void | VersionedResponse>;
  patientId: string;
  version: string;
}

const MedicalClearanceStatusCell = ({
  diagnosisOptions,
  id,
  inquiryId,
  medicalNotClearedReasonOptions,
  medicalReview,
  medicalStatusOptions,
  medicalTypeOptions,
  updateMedicalReviewStatus,
  updateMedicalReview,
  patientId,
  version,
}: MedicalClearanceStatusCellProps) => {
  const [isMedicalReviewModalOpen, setIsMedicalReviewModalOpen] =
    useState<boolean>(false);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState<boolean>(false);
  const [shouldFetchInquiryReviews, setShouldFetchInquiryReviews] =
    useState<boolean>(false);

  const { data: inquiryReviewResponse } = useGetInquiryReviewsQuery(inquiryId, {
    skip: !shouldFetchInquiryReviews || !inquiryId,
  });
  const medical = inquiryReviewResponse?.medical;
  const medicalStatus = medicalReview?.description;

  const medicalInitialState: MedicalReviewState =
    useMemo<MedicalReviewState>(() => {
      const {
        completedDate,
        requestedDate,
        type,
        status,
        notClearedReason,
        preliminaryDiagnosis,
        appointmentDate,
      } = medical ?? {};
      return {
        appointmentDate: toDate(appointmentDate),
        completedDate: toDate(completedDate),
        notClearedReason: notClearedReason?.description,
        preliminaryDiagnosis: preliminaryDiagnosis?.description,
        requestedDate: toDate(requestedDate),
        status: status?.description,
        type: type?.description,
      };
    }, [medical]);
  const [medicalEditState, updateMedicalEditState] = useReducer<
    (
      state: MedicalReviewState,
      action: MedicalReviewState,
    ) => MedicalReviewState
  >(reducer, {});

  const handleUpdateMedicalReviewStatus = useCallback(
    (optionId) => {
      const selectedStatus = medicalStatusOptions.find(
        (option) => option.id === optionId,
      )?.value;

      if (
        selectedStatus === MedicalStatusOptions.NOT_CLEARED ||
        selectedStatus === MedicalStatusOptions.CLEARED
      ) {
        setIsMedicalReviewModalOpen(true);
        return;
      }

      if (selectedStatus === medicalStatus) {
        return;
      }

      updateMedicalReviewStatus?.(
        inquiryId,
        patientId,
        selectedStatus,
        version,
      );
    },
    [
      updateMedicalReviewStatus,
      medicalStatusOptions,
      medicalStatus,
      inquiryId,
      patientId,
      version,
    ],
  );

  const updateMedicalClearance = useCallback(() => {
    setShouldFetchInquiryReviews(true);
    updateMedicalReview?.(inquiryId, medicalEditState, patientId, version).then(
      () => {
        setIsMedicalReviewModalOpen(false);
      },
    );
  }, [updateMedicalReview, inquiryId, medicalEditState, patientId, version]);

  const isMedicalStateChange = () =>
    !isEqualIgnoreNil(medicalInitialState, medicalEditState);

  const handleMedicalStatusModalCancel = () => {
    setShouldFetchInquiryReviews(true);
    if (isMedicalStateChange()) {
      setIsExitConfirmOpen(true);
    } else {
      setIsMedicalReviewModalOpen(false);
    }
  };

  const handleMedicalStatusExitConfirm = () => {
    setIsMedicalReviewModalOpen(false);
    setIsExitConfirmOpen(false);
  };

  const handleMedicalStatusClose = () => {
    setIsExitConfirmOpen(false);
  };

  useEffect(() => {
    if (inquiryReviewResponse) {
      setShouldFetchInquiryReviews(false);
    }
  }, [inquiryReviewResponse]);

  return (
    <>
      <StatusCell
        id={`${id}__medical-review-status-cell`}
        onOptionClick={handleUpdateMedicalReviewStatus}
        status={medicalStatus ?? MedicalStatusOptions.NOT_STARTED}
        statusOptions={medicalStatusOptions.map((option) => ({
          color: option.color as TagColor,
          id: option.id,
          value: option.value,
        }))}
      />
      <MedicalReviewModal
        diagnosisOptions={diagnosisOptions}
        isNotClearedReasonRequired={
          medicalEditState.status === MedicalStatusOptions.NOT_CLEARED
        }
        isOpen={isMedicalReviewModalOpen}
        isTypeRequired={
          medicalEditState.status !== MedicalStatusOptions.NOT_STARTED
        }
        notClearedReasons={medicalNotClearedReasonOptions}
        onCancel={handleMedicalStatusModalCancel}
        onChange={updateMedicalEditState}
        onConfirm={updateMedicalClearance}
        reviewTypeOptions={medicalTypeOptions}
        state={medicalEditState}
        statusOptions={medicalStatusOptions.map((option) => ({
          color: option.color as TagColor,
          id: option.id,
          value: option.value,
        }))}
      />
      <DiscardChangesModalV2
        isOpen={isExitConfirmOpen}
        onClose={handleMedicalStatusClose}
        onConfirm={handleMedicalStatusExitConfirm}
      />
    </>
  );
};

export default MedicalClearanceStatusCell;
